import type { ReservationWrapper } from "@b2bportal/core-types";
import { handleAxiosError } from "@b2bportal/core-utilities";
import {
  type GetHotelReservationRequest,
  type HotelItinerary,
  HotelItineraryState,
  type HotelItinerarySummary,
} from "@b2bportal/lodging-api";
import { Product } from "@b2bportal/purchase-api";
import { hotelsApi } from "@overrides/utilities";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import dayjs from "dayjs";

export interface IHotelItineraryResponse {
  itineraries: {
    [key: string]: Array<ReservationWrapper<HotelItinerarySummary>>;
  };
}

export const hotelsPostBookingApi = createApi({
  reducerPath: "hotelsPostBooking",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["HotelReservationList"],
  endpoints: (builder) => ({
    fetchAllHotelReservations: builder.query<IHotelItineraryResponse, void>({
      queryFn: async () => {
        try {
          const response = await hotelsApi.apiV0TripsHotelPut({
            states: [
              HotelItineraryState.Canceled,
              HotelItineraryState.Future,
              HotelItineraryState.Present,
              HotelItineraryState.Past,
            ],
            referenceDateTime: dayjs().format(),
          });

          const wrappedReservations = Object.keys(
            response.data.itineraries
          ).reduce(
            (acc, key) => {
              const reservations = response.data.itineraries[key];
              const wrapped = reservations.map((reservation) => ({
                reservation,
                reservationId: reservation.reservationBookingId,
                sortDate: reservation.checkInDate,
                recentFirst:
                  key === HotelItineraryState.Past ||
                  key === HotelItineraryState.Canceled,
                isCancelled: key === HotelItineraryState.Canceled,
                productType: Product.Hotel,
              }));
              return { ...acc, [key]: wrapped };
            },
            {} as {
              [key: string]: Array<ReservationWrapper<HotelItinerarySummary>>;
            }
          );

          return {
            data: {
              itineraries: wrappedReservations,
            },
          };
        } catch (error) {
          return handleAxiosError(error);
        }
      },
      providesTags: ["HotelReservationList"],
    }),
    fetchHotelReservation: builder.query<
      HotelItinerary,
      GetHotelReservationRequest
    >({
      queryFn: async (request) => {
        try {
          const response =
            await hotelsApi.apiV0TripsHotelGetReservationPut(request);

          return { data: response.data };
        } catch (error) {
          return handleAxiosError(error);
        }
      },
    }),
  }),
});
